import { useState } from "react";
import { TaskTemplate, TemplateTask, TaskTemplateInstance } from "@/lib/api/checklist/templates";
import { 
  TemplateList, 
  TemplateDetailView, 
  TemplateInstanceList,
  CreateEditTemplateModal,
  CreateEditTemplateTaskModal,
  TemplateInstanceDetailModal,
  TriggerTemplateModal,
} from "./";
import { useDeleteTemplate, useDeleteTemplateTask } from "@/lib/api/checklist/templates";
import { toast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQueryParam } from "@/hooks/use-query-param";
import { useSearchParams } from "react-router-dom";

export function TemplatesDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templateTab, setTemplateTab] = useQueryParam("template_tab", "templates");
  const [viewTemplateId, setViewTemplateId] = useQueryParam("template_id", "");
  const [templateInstanceId, setTemplateInstanceId] = useQueryParam("template_instance_id", "");
  const [taskIdParam, setTaskIdParam] = useQueryParam("template_task_id", "");
  
  // Function to clear multiple query params at once
  const clearInstanceModalParams = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("template_instance_id");
    newParams.delete("template_task_id");
    newParams.delete("template_id");
    setSearchParams(newParams, { replace: true });
  };
  
  // Template states
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<TaskTemplate | undefined>();
  const [deleteTemplate, setDeleteTemplate] = useState<TaskTemplate | undefined>();
  const [triggerTemplate, setTriggerTemplate] = useState<TaskTemplate | null>(null);
  
  // Template task states
  const [createTaskTemplateId, setCreateTaskTemplateId] = useState<string | undefined>();
  const [editTemplateTask, setEditTemplateTask] = useState<TemplateTask | undefined>();
  const [deleteTemplateTask, setDeleteTemplateTask] = useState<{ task: TemplateTask; templateId: string } | undefined>();
  
  // Instance states
  const [viewInstance, setViewInstance] = useState<TaskTemplateInstance | undefined>();
  
  const deleteTemplateMutation = useDeleteTemplate();
  const deleteTemplateTaskMutation = useDeleteTemplateTask(deleteTemplateTask?.templateId || "");

  const handleDeleteTemplate = async () => {
    if (!deleteTemplate) return;
    
    try {
      const result = await deleteTemplateMutation.mutateAsync(deleteTemplate.id);
      if (!result.success) throw result;
      toast({
        title: "Template deleted",
        description: "The template has been deleted successfully.",
      });
      setDeleteTemplate(undefined);
      if (viewTemplateId === deleteTemplate.id) {
        setViewTemplateId("");
        setTemplateTab("templates");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete template.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTemplateTask = async () => {
    if (!deleteTemplateTask) return;
    
    try {
      const result = await deleteTemplateTaskMutation.mutateAsync(deleteTemplateTask.task.id);
      if (!result.success) throw result;

      toast({
        title: "Task deleted",
        description: "The template task has been deleted successfully.",
      });
      setDeleteTemplateTask(undefined);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete template task.",
        variant: "destructive",
      });
    }
  };

  const handleViewTemplate = (template: TaskTemplate) => {
    setViewTemplateId(template.id);
  };

  const handleViewInstance = (instance: TaskTemplateInstance) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("template_instance_id", instance.id);
    newParams.set("template_id", instance.template_id);
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div className="space-y-6">
      {!templateInstanceId && viewTemplateId ? (
        <TemplateDetailView
          templateId={viewTemplateId}
          onBack={() => {
            setViewTemplateId("");
          }}
          onEditTemplate={setEditTemplate}
          onAddTask={() => setCreateTaskTemplateId(viewTemplateId)}
          onEditTask={(task) => {
            setEditTemplateTask(task);
            setCreateTaskTemplateId(viewTemplateId);
          }}
          onDeleteTask={(task) => setDeleteTemplateTask({ task, templateId: viewTemplateId })}
          onTriggerTemplate={setTriggerTemplate}
        />
      ) : !templateInstanceId ? (
        <Tabs value={templateTab} onValueChange={setTemplateTab}>
          <TabsList>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="instances">Active Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-6">
            <TemplateList
              onViewTemplate={handleViewTemplate}
              onEditTemplate={setEditTemplate}
              onDeleteTemplate={setDeleteTemplate}
              onTriggerTemplate={setTriggerTemplate}
              onCreateTemplate={() => setCreateTemplateOpen(true)}
            />
          </TabsContent>

          <TabsContent value="instances" className="mt-6">
            <TemplateInstanceList
              onViewInstance={handleViewInstance}
            />
          </TabsContent>
        </Tabs>
      ) : null}

      {/* Template Modals */}
      <CreateEditTemplateModal
        open={createTemplateOpen || !!editTemplate}
        onClose={() => {
          setCreateTemplateOpen(false);
          setEditTemplate(undefined);
        }}
        template={editTemplate}
      />

      {createTaskTemplateId && (
        <CreateEditTemplateTaskModal
          open={!!createTaskTemplateId}
          onClose={() => {
            setCreateTaskTemplateId(undefined);
            setEditTemplateTask(undefined);
          }}
          templateId={createTaskTemplateId}
          task={editTemplateTask}
        />
      )}

      {/* Delete Dialogs */}
      <Dialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the template "{deleteTemplate?.name}"? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplate(undefined)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTemplate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTemplateMutation.isLoading}
            >
              {deleteTemplateMutation.isLoading ? "Deleting..." : "Delete Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTemplateTask} onOpenChange={() => setDeleteTemplateTask(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the task "{deleteTemplateTask?.task.title}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplateTask(undefined)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteTemplateTask}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteTemplateTaskMutation.isLoading}
            >
              {deleteTemplateTaskMutation.isLoading ? "Deleting..." : "Delete Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Instance Detail Modal */}
      {templateInstanceId && (
        <TemplateInstanceDetailModal
          open={!!templateInstanceId}
          onClose={clearInstanceModalParams}
          templateInstanceId={templateInstanceId}
          templateId={viewTemplateId}
          highlightTaskId={taskIdParam}
        />
      )}

      {/* Trigger Template Modal */}
      {triggerTemplate && (
        <TriggerTemplateModal
          open={!!triggerTemplate}
          onClose={() => setTriggerTemplate(null)}
          template={triggerTemplate}
        />
      )}
    </div>
  );
}
